import { FirebaseCachedImage } from "@components/FirebaseCachedImage";
import { ImageIcon } from "@components/Icons/Image";
import { VideoIcon } from "@components/Icons/Video";
import { FullPicker } from "@components/ImagePicker/FullPicker";
import { MediaCarousel } from "@components/MediaCarousel";
import { Share } from "@components/Share";
import ReactVideoRecorder from "@components/VideoRecorder/video-recorder";
import { Timestamp, doc, updateDoc, getDoc, writeBatch } from "@firebase/firestore";
import { extractFrameFromVideoBlob } from "@helpers/extractFrameFromVideoBlob";
import { MAX_VIDEO_FILE_SIZE } from "@helpers/fileLimits";
import { useTranslatedKey } from "@helpers/useTranslatedKey";
import { useTranslation } from "@helpers/useTranslation";
import { useEvent } from "@helpers/useEvent";
import { useProgressStore } from "@pages/challenges";
import { ref as storageRef, uploadBytes, uploadBytesResumable, getBlob, ref } from "firebase/storage";
import { FC, useContext, useEffect, useState, useMemo } from "react";
import { useHttpsCallable } from "react-firebase-hooks/functions";
import { FirebaseContext } from "src/helpers/firebase";
import { useConfetti } from "src/helpers/useConfetti";
import { useEventNavigate } from "src/helpers/useEventNavigate";
import { useTeam } from "src/helpers/useTeam";
import { useToast } from "src/helpers/useToast";
import { createExtraMapEntry, getChallengeLinkedExtras } from "src/helpers/extrasMapHelper";
import { uid } from "src/helpers/uid";

type Props = {
  challenge: PhotoVideoChallenge;
  completed: boolean;
  teamId: string;
  eventId: string;
};

type UploadTab = "photo" | "video";

export const PhotoVideo: FC<Props> = (props) => {
  const { id } = props.challenge;
  const { completed, eventId, teamId } = props;
  const { t } = useTranslation("challenges");
  const { t: uploadT } = useTranslation("upload");
  const { firestore, functions, storage } = useContext(FirebaseContext);
  const [image, setImage] = useState(undefined);
  const [video, setVideo] = useState(undefined);
  const [frame, setFrame] = useState(null);
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const [event] = useEvent();
  const navigate = useEventNavigate();
  const [team] = useTeam();
  const [submitting, setSubmitting] = useState(false);
  const [uploadingMore, setUploadingMore] = useState(false);
  const [showMoreUpload, setShowMoreUpload] = useState(false);
  const [additionalImages, setAdditionalImages] = useState<File[]>([]);
  const [uploadKey, setUploadKey] = useState(0);
  const [activeTab, setActiveTab] = useState<UploadTab>("photo");
  const [extraActiveTab, setExtraActiveTab] = useState<UploadTab>("photo");
  useConfetti("submit-button");
  const toast = useToast();
  const [updateTeamPoints, _, error] = useHttpsCallable<API.UpdateTeamPointsRequest, API.UpdateTeamPointsResponse>(
    functions,
    "updateTeamPointsV2",
  );

  // @ts-ignore
  const setChallengeLoadingState = useProgressStore((state) => state.setChallenge);

  const { getTranslatedValue } = useTranslatedKey();
  const title = getTranslatedValue(props.challenge, "title");
  const copy = getTranslatedValue(props.challenge, "copy");

  // Get challenge-linked extras for this challenge
  const challengeExtras = useMemo(() => {
    if (!team?.extrasMap) return [];
    return getChallengeLinkedExtras(team.extrasMap, id);
  }, [team?.extrasMap, id]);

  // Detect if the first solution was a video
  const solutionIsVideo = !!team?.solutionsMap?.[`${id}-video`];

  // Check which extras have video
  const extraHasVideo = (extraId: string) => !!team?.extrasMap?.[`${extraId}-video`];

  const carouselItems: MediaItem[] = useMemo(() => {
    const items: MediaItem[] = [
      {
        id,
        type: solutionIsVideo ? "video" : "photo",
        uri: `events/${eventId}/teams/${teamId}/solutions/${id}`,
      },
    ];
    challengeExtras.forEach((extraId) => {
      items.push({
        id: extraId,
        type: extraHasVideo(extraId) ? "video" : "photo",
        uri: `events/${eventId}/teams/${teamId}/extras/${extraId}`,
      });
    });
    return items;
  }, [id, eventId, teamId, challengeExtras, solutionIsVideo]);

  useEffect(() => {
    if (error) {
      toast(t("problemOccured"), "🤷‍♂️", "error");
      console.error("Problem occured in updateTeamPoints function: ", error);
    }
  }, [error]);

  const getFrame = async (videoBlob: Blob): Promise<Blob> => {
    let extractionFailed = false;
    let blob: Blob;
    try {
      blob = await extractFrameFromVideoBlob(videoBlob, 1);
    } catch (error) {
      extractionFailed = true;
    }

    extractionFailed = extractionFailed || (blob as File).size < 1024;

    if (extractionFailed) {
      const backgroundUri = `events/${eventId}/background_light`;
      const backgroundRef = ref(storage, backgroundUri);
      try {
        blob = await getBlob(backgroundRef);
      } catch (error) {
        const defaultUrl = event?.type === "wedding" ? "/bg-wedding.webp" : "/bg-conference.webp";
        const response = await fetch(defaultUrl);
        if (response.ok) {
          blob = await response.blob();
        }
      }
    }
    return blob;
  };

  const handleVideoInput = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const target = e.target;
    const files = target.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.size > MAX_VIDEO_FILE_SIZE) {
        toast(t("videoFileTooLarge"), "🤷‍♂️", "error");
        return;
      }

      try {
        const videoBlob = new Blob([file], { type: file.type });
        setVideo(videoBlob);
        setUploadedFileName(file.name);
        const extractedFrame = await getFrame(videoBlob);
        setFrame(extractedFrame);
      } catch (error) {
        console.error(error);
      }
    }
  };

  const updatePromisePhoto = async () => {
    try {
      setChallengeLoadingState(id, true);
      const imagePath = `events/${eventId}/teams/${teamId}/solutions/${id}`;
      const imageRef = storageRef(storage, imagePath);
      await uploadBytes(imageRef, image);

      await updateTeamPoints({ teamId, challengeId: id, eventId, solutionType: "photovideo" });
      const teamDocRef = doc(firestore, `events/${eventId}/teams/${teamId}`);

      const teamDoc = await getDoc(teamDocRef);
      const teamData = teamDoc.data();
      const solutionsMap = teamData?.solutionsMap || {};
      solutionsMap[id] = Timestamp.now();
      await updateDoc(teamDocRef, { solutionsMap });
    } catch (error) {
      toast(t("problemOccured"), "🤷‍♂️", "error");
      console.error(error);
    } finally {
      await new Promise((resolve) => setTimeout(resolve, 2000));
      setChallengeLoadingState(id, false);
    }
  };

  const updatePromiseVideo = async () => {
    try {
      setChallengeLoadingState(id, true);

      const framePath = `events/${eventId}/teams/${teamId}/solutions/${id}`;
      const frameRef = storageRef(storage, framePath);
      await uploadBytes(frameRef, frame);

      const videoPath = `events/${eventId}/teams/${teamId}/solutions/${id}.mp4`;
      const videoRef = storageRef(storage, videoPath);
      // Resumable upload: retries in chunks, needed for gallery videos up to MAX_VIDEO_FILE_SIZE
      await uploadBytesResumable(videoRef, video);

      await updateTeamPoints({ teamId, challengeId: id, eventId, solutionType: "photovideo" });
      const teamDocRef = doc(firestore, `events/${eventId}/teams/${teamId}`);

      try {
        const teamDoc = await getDoc(teamDocRef);
        const teamData = teamDoc.data();
        const solutionsMap = teamData?.solutionsMap || {};
        solutionsMap[id] = Timestamp.now();
        solutionsMap[`${id}-video`] = Timestamp.now();
        await updateDoc(teamDocRef, { solutionsMap });
      } catch (error) {
        console.error("Error updating solutions map: ", error);
        if (error.message.includes("PERMISSION_DENIED")) toast(t("sessionExpired"), "🤷‍♂️", "error");
        else toast(t("somethingWentWrong"), "🤷‍♂️", "error");
      }
    } catch (error) {
      toast(t("problemOccured"), "🤷‍♂️", "error");
      console.error(error);
    } finally {
      await new Promise((resolve) => setTimeout(resolve, 2000));
      setChallengeLoadingState(id, false);
    }
  };

  const submit = async () => {
    if (!team) {
      toast(t("problemOccured"), "🤷‍♂️", "error");
      console.error(`Team is undefined. Team: ${team}, event: ${eventId}`);
      return;
    }

    setSubmitting(true);
    if (activeTab === "photo" && image) {
      updatePromisePhoto();
    } else if (activeTab === "video" && video && frame) {
      updatePromiseVideo();
    }
    navigate("/challenges");
  };

  const uploadMorePhoto = async () => {
    if (!team || !additionalImages[0]) {
      toast(t("problemOccured"), "🤷‍♂️", "error");
      return;
    }

    setUploadingMore(true);

    try {
      const extraId = uid();
      const imagePath = `events/${eventId}/teams/${teamId}/extras/${extraId}`;
      const imageRef = storageRef(storage, imagePath);
      await uploadBytes(imageRef, additionalImages[0]);

      const batch = writeBatch(firestore);
      const docRef = doc(firestore, `events/${eventId}/teams/${teamId}/extras/${extraId}`);
      batch.set(docRef, {
        id: extraId,
        createdOn: Timestamp.now(),
        challengeId: id,
      });

      const teamDocRef = doc(firestore, `events/${eventId}/teams/${teamId}`);
      const teamDoc = await getDoc(teamDocRef);
      const teamData = teamDoc.data();
      const extrasMap = teamData?.extrasMap || {};
      extrasMap[extraId] = createExtraMapEntry(id);
      await updateDoc(teamDocRef, { extrasMap });
      await batch.commit();

      setUploadKey((prev) => prev + 1);
      await new Promise((resolve) => setTimeout(resolve, 2000));
      toast(t("photoUploaded") || "Photo uploaded!", "📸", "success");
      setAdditionalImages([]);
    } catch (error) {
      console.error("Error uploading more photo:", error);
      toast(t("problemOccured"), "🤷‍♂️", "error");
    } finally {
      setUploadingMore(false);
    }
  };

  const [extraVideo, setExtraVideo] = useState(undefined);
  const [extraFrame, setExtraFrame] = useState(null);

  const handleExtraVideoInput = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.size > MAX_VIDEO_FILE_SIZE) {
        toast(t("videoFileTooLarge"), "🤷‍♂️", "error");
        return;
      }

      try {
        const videoBlob = new Blob([file], { type: file.type });
        setExtraVideo(videoBlob);
        const extractedFrame = await getFrame(videoBlob);
        setExtraFrame(extractedFrame);
      } catch (error) {
        console.error(error);
      }
    }
  };

  const uploadMoreVideo = async () => {
    if (!team || !extraVideo || !extraFrame) {
      toast(t("problemOccured"), "🤷‍♂️", "error");
      return;
    }

    setUploadingMore(true);

    try {
      const extraId = uid();

      const framePath = `events/${eventId}/teams/${teamId}/extras/${extraId}`;
      const frameRef = storageRef(storage, framePath);
      await uploadBytes(frameRef, extraFrame);

      const videoPath = `events/${eventId}/teams/${teamId}/extras/${extraId}.mp4`;
      const videoRef = storageRef(storage, videoPath);
      await uploadBytesResumable(videoRef, extraVideo);

      const batch = writeBatch(firestore);
      const docRef = doc(firestore, `events/${eventId}/teams/${teamId}/extras/${extraId}`);
      batch.set(docRef, {
        id: extraId,
        createdOn: Timestamp.now(),
        challengeId: id,
        hasVideo: true,
      });

      const teamDocRef = doc(firestore, `events/${eventId}/teams/${teamId}`);
      const teamDoc = await getDoc(teamDocRef);
      const teamData = teamDoc.data();
      const extrasMap = teamData?.extrasMap || {};
      extrasMap[extraId] = createExtraMapEntry(id);
      extrasMap[`${extraId}-video`] = createExtraMapEntry(id);
      await updateDoc(teamDocRef, { extrasMap });
      await batch.commit();

      await new Promise((resolve) => setTimeout(resolve, 2000));
      toast(t("videoUploaded") || "Video uploaded!", "🎬", "success");
      setExtraVideo(undefined);
      setExtraFrame(null);
    } catch (error) {
      console.error("Error uploading more video:", error);
      toast(t("problemOccured"), "🤷‍♂️", "error");
    } finally {
      setUploadingMore(false);
    }
  };

  const canSubmit = activeTab === "photo" ? !!image : !!(video && frame);

  const mimeType = "video/mp4";

  const TabButtons: FC<{ active: UploadTab; onSelect: (tab: UploadTab) => void }> = ({ active, onSelect }) => (
    <div className="mb-6 flex justify-center gap-4">
      <button
        role="tab"
        className={`flex h-20 w-20 flex-col items-center justify-center rounded-xl border transition-colors duration-150
          ${
            active === "photo"
              ? "border-primary bg-primary/10 text-primary"
              : "border-base-200 bg-base-100 text-base-content/60 hover:bg-base-200"
          }`}
        onClick={() => onSelect("photo")}
      >
        <ImageIcon className={`mb-1 h-6 w-6 ${active === "photo" ? "text-primary" : "text-base-content/60"}`} />
        <span className={`text-xs font-medium ${active === "photo" ? "text-primary" : "text-base-content/50"}`}>
          {t("photoTab")}
        </span>
      </button>
      <button
        role="tab"
        className={`flex h-20 w-20 flex-col items-center justify-center rounded-xl border transition-colors duration-150
          ${
            active === "video"
              ? "border-primary bg-primary/10 text-primary"
              : "border-base-200 bg-base-100 text-base-content/60 hover:bg-base-200"
          }`}
        onClick={() => onSelect("video")}
      >
        <VideoIcon className={`mb-1 h-6 w-6 ${active === "video" ? "text-primary" : "text-base-content/60"}`} />
        <span className={`text-xs font-medium ${active === "video" ? "text-primary" : "text-base-content/50"}`}>
          {t("videoTab")}
        </span>
      </button>
    </div>
  );

  return (
    <>
      <h1 className="mx-4 mt-16 mb-8 text-center text-4xl font-semibold">{title}</h1>
      <div className="mt-4 mb-8 flex flex-col items-center">
        <div className="mx-6">
          {completed ? (
            <div className="flex flex-col items-center">
              <div className="flex max-w-lg flex-col justify-center">
                <h2 className="text-center text-xl font-bold">{t("challengeDone")}</h2>
                <div className="mt-4 mb-4 text-center text-lg">{t("challengeDoneSummary")}</div>
              </div>
              <div className="px-12 py-6">
                <Share title={title} text={title} uri={`events/${eventId}/teams/${teamId}/solutions/${id}`} />

                <MediaCarousel items={carouselItems} />

                {/* Add more media button */}
                {!showMoreUpload && (
                  <div className="mt-6 flex justify-center">
                    <button onClick={() => setShowMoreUpload(true)} className="btn-outline btn">
                      {t("addMoreMedia") || "Add More"}
                    </button>
                  </div>
                )}

                {/* Additional media upload UI */}
                {showMoreUpload && (
                  <div className="mt-6">
                    <p className="mb-4 text-center text-sm opacity-70">
                      {t("addMoreMediaDescription") || "Upload additional photos or videos for this challenge."}
                    </p>

                    <TabButtons active={extraActiveTab} onSelect={setExtraActiveTab} />

                    {extraActiveTab === "photo" && (
                      <>
                        <FullPicker
                          key={uploadKey}
                          uploadLabel={t("selectPhoto") || "Select Photo"}
                          reuploadLabel={t("selectDifferentPhoto") || "Select Different Photo"}
                          onNewImage={(img) => {
                            if (img) setAdditionalImages([img]);
                          }}
                          allowUpload
                        />
                        <div className="mt-4 flex justify-center gap-4">
                          <button
                            disabled={additionalImages.length === 0 || uploadingMore}
                            onClick={uploadMorePhoto}
                            className={`btn-primary btn ${uploadingMore ? "loading" : ""}`}
                          >
                            {t("uploadPhoto") || "Upload Photo"}
                          </button>
                          <button
                            onClick={() => {
                              setShowMoreUpload(false);
                              setAdditionalImages([]);
                            }}
                            className="btn-outline btn"
                            disabled={uploadingMore}
                          >
                            {t("done") || "Done"}
                          </button>
                        </div>
                      </>
                    )}

                    {extraActiveTab === "video" && (
                      <>
                        <div className="flex w-full flex-col items-center">
                          <input
                            onChange={handleExtraVideoInput}
                            type="file"
                            accept="video/*"
                            className="w-full rounded-lg border border-primary bg-white p-3 text-sm text-primary"
                          />
                        </div>
                        <div className="mt-4 flex justify-center gap-4">
                          <button
                            disabled={!extraVideo || !extraFrame || uploadingMore}
                            onClick={uploadMoreVideo}
                            className={`btn-primary btn ${uploadingMore ? "loading" : ""}`}
                          >
                            {t("submit")}
                          </button>
                          <button
                            onClick={() => {
                              setShowMoreUpload(false);
                              setExtraVideo(undefined);
                              setExtraFrame(null);
                            }}
                            className="btn-outline btn"
                            disabled={uploadingMore}
                          >
                            {t("done") || "Done"}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <>
              <p className="text-md mt-6 mb-4 text-center">{copy}</p>

              {props.challenge.introPhotoUri && (
                <div className="mb-4 flex justify-center">
                  <div className="w-full max-w-xs">
                    <FirebaseCachedImage uri={props.challenge.introPhotoUri} alt={title} />
                  </div>
                </div>
              )}

              <TabButtons active={activeTab} onSelect={setActiveTab} />

              {activeTab === "photo" && (
                <FullPicker uploadLabel={t("upload")} reuploadLabel={t("reupload")} onNewImage={setImage} allowUpload />
              )}

              {activeTab === "video" && (
                <div className="flex w-full justify-center">
                  <div className="h-[50vh] w-72">
                    <ReactVideoRecorder
                      mimeType={mimeType}
                      showReplayControls={true}
                      t={uploadT}
                      renderErrorView={({ error: recorderError }) => (
                        <div className="flex w-full flex-col justify-center">
                          <div className="mx-8 flex flex-col justify-center">
                            {recorderError?.name === "NotAllowedError" ? (
                              <>
                                <div className="mb-4 text-lg">Camera access denied</div>
                                <div className="mb-6 text-sm text-gray-300">
                                  Please enable camera permissions in your browser settings, then try again or upload a
                                  video from your device.
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="mb-6 text-lg">{t("uploadVideoFromDevice")}</div>
                                <div className="text-xs text-gray-300">{t("uploadVideoNote")}</div>
                              </>
                            )}
                          </div>
                        </div>
                      )}
                      timeLimit={60000}
                      isFlipped={false}
                      constraints={{
                        audio: true,
                      }}
                      renderDisconnectedView={() => <></>}
                      onRecordingComplete={async (videoBlob: Blob) => {
                        setVideo(videoBlob);
                        setUploadedFileName(null);
                        const extractedFrame = await getFrame(videoBlob);
                        setFrame(extractedFrame);
                      }}
                      onUpload={submit}
                      showSubmitButton={false}
                    />
                  </div>
                </div>
              )}

              {activeTab === "video" && (
                <div className="mx-auto mt-6 flex w-72 flex-col items-center">
                  <div className="mb-2 text-center text-sm opacity-70">{t("orUploadVideo")}</div>
                  <input
                    onChange={handleVideoInput}
                    type="file"
                    accept="video/*"
                    className="w-full rounded-lg border border-primary bg-white p-3 text-sm text-primary"
                  />
                  {uploadedFileName && (
                    <div className="mt-2 text-center text-sm text-success">✓ {t("videoSelected")}</div>
                  )}
                </div>
              )}
            </>
          )}
          {!completed && (
            <div className="mt-8 mb-12 flex justify-center">
              <button
                disabled={!canSubmit || submitting}
                onClick={submit}
                className={`btn-primary btn ${submitting ? "loading" : ""}`}
                id="submit-button"
              >
                {t("submit")}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
